import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { getConfig, resolveTheme } from '../config';
import { store, type AccountView } from '../store';
import { CartPage } from './CartPage';
import { t } from '../i18n';
import { isInlineViewOnCurrentPage } from '../inlinePages';
import { lockBodyScroll, unlockBodyScroll } from '../utils/scrollLock';

function isDrawerMode(): boolean {
  return getConfig().cartOpenMode === 'side_drawer';
}

function resolveDrawerThemeStyle(): Record<string, string> {
  const config = getConfig();
  const theme = resolveTheme(config.themePreset, config.theme);
  const map: Record<string, string> = {
    primary: '--sw-primary',
    secondary: '--sw-secondary',
    bg: '--sw-bg',
    text: '--sw-text',
    muted: '--sw-muted',
    border: '--sw-border',
    radius: '--sw-radius',
    radiusModal: '--sw-radius-modal',
    font: '--sw-font',
    fontSize: '--sw-font-size',
    lineHeight: '--sw-line-height',
    fontWeight: '--sw-font-weight',
    letterSpacing: '--sw-letter-spacing',
    fontSizeSm: '--sw-font-size-sm',
    font_size_sm: '--sw-font-size-sm',
    shadow: '--sw-shadow',
    shadowSm: '--sw-shadow-sm',
    shadowModal: '--sw-shadow-modal',
    surface_bg: '--sw-surface-bg',
    surface_border: '--sw-surface-border',
    surface_shadow: '--sw-surface-shadow',
    button_bg: '--sw-button-bg',
    button_background_color: '--sw-button-bg',
    button_text: '--sw-button-text',
    button_text_color: '--sw-button-text',
    button_border: '--sw-button-border',
    button_border_color: '--sw-button-border',
    button_hover_bg: '--sw-button-hover-bg',
    button_hover_text: '--sw-button-hover-text',
    button_hover_border: '--sw-button-hover-border',
    button_focus_bg: '--sw-button-focus-bg',
    button_focus_text: '--sw-button-focus-text',
    button_active_bg: '--sw-button-active-bg',
    button_active_text: '--sw-button-active-text',
  };
  const style: Record<string, string> = {};
  for (const [key, cssVar] of Object.entries(map)) {
    const value = (theme as Record<string, unknown>)[key];
    if (value != null && value !== '') style[cssVar] = String(value);
  }
  return style;
}

export function CartDrawerRoot() {
  const [view, setView] = useState<AccountView>(store.getAccountView());
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [backdropReady, setBackdropReady] = useState(false);
  useEffect(() => store.subscribe(() => setView(store.getAccountView())), []);

  const open = isDrawerMode() && view === 'cart' && !isInlineViewOnCurrentPage('cart');
  const close = () => store.setAccountView(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      setBackdropReady(false);
      const id = window.setTimeout(() => setBackdropReady(true), 320);
      return () => window.clearTimeout(id);
    }
    if (!mounted) return;
    setClosing(true);
    const timeoutId = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, 340);
    return () => window.clearTimeout(timeoutId);
  }, [open, mounted]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockBodyScroll();
    };
  }, [mounted]);

  if (!mounted) return null;

  const drawer = (
    <div class={`sw-cart-drawer-layer${closing ? ' is-closing' : ''}`} aria-modal="true" role="dialog" aria-label={t('title.cart')}>
      <button type="button" class="sw-cart-drawer-backdrop" onClick={() => { if (backdropReady) close(); }} aria-label={t('close')} />
      <aside class="sw-cart-drawer-panel">
        <div class="sw-cart-drawer-header">
          <strong>{t('title.cart')}</strong>
          <button type="button" class="btn-close" onClick={close} aria-label={t('close')}></button>
        </div>
        <div class="sw-cart-drawer-body">
          <CartPage forceClassic />
        </div>
      </aside>
    </div>
  );
  // Portal is mounted outside widget roots, so we wrap it with `shop-widget-mount`
  // to preserve scoped widget styles/tokens (positioning, animations, colors).
  return createPortal(<div class="shop-widget-mount" style={resolveDrawerThemeStyle()}>{drawer}</div>, document.body);
}
